// "What moved" for Security Operations (/soc): what changed per area over the selected trend window,
// from GET /soc/trends. Each tile says what it measures, so a net change is never presented as new
// detections:
//   kind "new"            - items first raised in the window (incidents + alerts, vulnerabilities)
//   kind "openedResolved" - opened and resolved in the window, and their difference
//   kind "events"         - external threat events in the window (+ new external risks)
//   kind "change"         - identity exposure (critical + high) at the last snapshot minus the first
// A tile appears only when its source is connected (the same gating as the Trends charts); an area
// whose data is stale shows why instead of a number.
// `view` rows open their area's view on the page (the left menu's Incidents & alerts, Endpoints,
// External threat, Network, Identities); the incident and external views show their part of the
// queue. Control alerts have no view: they belong to GRC.

import { freshnessOf, isConnected } from "./socFreshness";
import { EXTERNAL_BANDS } from "./SocTrendCharts";

const sum = (points, pick) => (points || []).reduce((acc, p) => acc + (Number(pick(p)) || 0), 0);
const hasKey = (tile, key) => Object.prototype.hasOwnProperty.call(tile?.metrics || {}, key);

export const formatNet = (n) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : "0");

// `previousValue`: the same measure over the previous period of equal length (trends.previous), for the
// row's change marker; null when the response has none or the measure is a snapshot change.
export const buildMoved = (tiles, trends, now = new Date()) => {
  if (!trends) return [];
  const prev = trends.previous || null;
  const num = (v) => Number(v) || 0;
  const byKey = Object.fromEntries((tiles || []).map((t) => [t.key, t]));
  const out = [];
  const push = (tileKey, tile) => {
    const fresh = tileKey ? freshnessOf(byKey[tileKey], now) : { state: "fresh" };
    out.push({ ...tile, stale: fresh.state === "stale", lastSync: fresh.lastSync || null });
  };

  if (isConnected(byKey.incidents)) {
    const n = sum(trends.newFindings, (p) => (Number(p.incidents) || 0) + (Number(p.alerts) || 0));
    push("incidents", {
      key: "incidents", label: "Incidents & alerts", kind: "new", value: n,
      previousValue: prev ? num(prev.newFindings?.incidents) + num(prev.newFindings?.alerts) : null,
      headline: `+${n}`, detail: `${n} new ${n === 1 ? "incident or alert" : "incidents and alerts"}`,
      source: "Defender XDR", queueArea: null, view: "incidents",
    });
  }

  if (isConnected(byKey.endpoint) && hasKey(byKey.endpoint, "openCves")) {
    const n = sum(trends.newFindings, (p) => p.vulnerabilities);
    push("endpoint", {
      key: "endpoint", label: "Endpoint", kind: "new", value: n,
      previousValue: prev ? num(prev.newFindings?.vulnerabilities) : null,
      // The trend's per-device count of new CVEs and recommendations, not unique CVEs.
      headline: `+${n}`, detail: `${n} ${n === 1 ? "vulnerability or recommendation" : "vulnerabilities and recommendations"} first recorded on devices, including any since closed`,
      source: "Defender for Endpoint", queueArea: null, view: "endpoint",
    });
  }

  const external = byKey.external;
  // The same bands the External activity chart draws, so the two numbers always agree.
  const bands = isConnected(external) ? EXTERNAL_BANDS.filter((b) => hasKey(external, b.metricKey)) : [];
  if (bands.length > 0) {
    const events = sum(trends.externalEvents, (p) => bands.reduce((acc, b) => acc + (Number(p[b.field]) || 0), 0));
    const newRisks = sum(trends.newFindings, (p) => p.external);
    push("external", {
      key: "external", label: "External threat", kind: "events", value: events,
      previousValue: prev ? bands.reduce((acc, b) => acc + num(prev.externalEvents?.[b.field]), 0) : null,
      headline: String(events),
      detail: `${events} ${events === 1 ? "event" : "events"} · ${newRisks} new ${newRisks === 1 ? "risk" : "risks"}`,
      source: "Surface, dark web, secrets, brand", queueArea: null, view: "external",
    });
  }

  if (isConnected(byKey.network)) {
    const opened = sum(trends.networkSignals, (p) => p.opened);
    const resolved = sum(trends.networkSignals, (p) => p.resolved);
    push("network", {
      key: "network", label: "Network", kind: "openedResolved", value: opened - resolved,
      previousValue: prev ? num(prev.networkSignals?.opened) - num(prev.networkSignals?.resolved) : null,
      headline: formatNet(opened - resolved), detail: `${opened} opened · ${resolved} resolved`,
      source: "Fortinet", queueArea: null, view: "network",
    });
  }

  // Control alerts are part of the GRC module the page already requires, so they are always shown.
  {
    const opened = sum(trends.controlAlerts, (p) => p.opened);
    const resolved = sum(trends.controlAlerts, (p) => p.resolved);
    push(null, {
      key: "controls", label: "Control alerts", kind: "openedResolved", value: opened - resolved,
      previousValue: prev ? num(prev.controlAlerts?.opened) - num(prev.controlAlerts?.resolved) : null,
      headline: formatNet(opened - resolved), detail: `${opened} raised · ${resolved} resolved`,
      source: "GRC controls", queueArea: null, view: null,
    });
  }

  const snapshots = trends.identityExposure || [];
  if (hasKey(byKey.identity, "identities") && snapshots.length >= 2) {
    const urgent = (s) => (Number(s.critical) || 0) + (Number(s.high) || 0);
    const first = urgent(snapshots[0]);
    const last = urgent(snapshots[snapshots.length - 1]);
    push("identity", {
      key: "identity", label: "Identity", kind: "change", value: last - first, previousValue: null,
      headline: formatNet(last - first), detail: `Critical + high exposed identities: ${first} → ${last}`,
      source: "Identity exposure", queueArea: null, view: "identity",
    });
  }

  return out;
};
